import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Package, Clock, CheckCircle, XCircle, Truck, AlertTriangle,
  Loader2, Phone, MessageSquare, Search, QrCode, ChevronRight,
  Timer, RefreshCw, Home, Store, Share2, Camera, X as CloseIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { API_URL } from '../utils/apiConfig';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

// Status colors and labels
const STATUS_CONFIG = {
  pending_approval: { color: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Esperando Aprobación', labelEn: 'Waiting for Approval', icon: Clock },
  approved: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Aprobado', labelEn: 'Approved', icon: CheckCircle },
  declined: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Rechazado', labelEn: 'Declined', icon: XCircle },
  expired: { color: 'bg-slate-100 text-slate-800 border-slate-200', label: 'Expirado', labelEn: 'Expired', icon: Timer },
  cancelled: { color: 'bg-slate-100 text-slate-600 border-slate-200', label: 'Cancelado', labelEn: 'Cancelled', icon: XCircle },
};

const DELIVERY_STATUS_CONFIG = {
  none: { label: 'Pendiente', labelEn: 'Pending' },
  preparing: { label: 'Preparando', labelEn: 'Preparing' },
  out_for_delivery: { label: 'En Camino', labelEn: 'Out for Delivery' },
  delivered: { label: 'Entregado', labelEn: 'Delivered' },
  completed: { label: 'Completado', labelEn: 'Completed' },
};

export default function MerchantPurchaseRequests() {
  const { language } = useLanguage();
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [pollingRequest, setPollingRequest] = useState(null);
  const [activeTab, setActiveTab] = useState('pending'); // pending | approved | history

  // Translations
  const t = {
    es: {
      title: 'Solicitudes de Compra',
      newPurchase: 'Nueva Compra',
      pending: 'Pendientes',
      approved: 'Aprobadas',
      history: 'Historial',
      noRequests: 'Sin solicitudes pendientes',
      createFirst: 'Crea tu primera solicitud de compra',
      viewDetails: 'Ver detalles',
      okToDeliver: 'LISTO PARA ENTREGAR',
      doNotProcess: 'NO PROCESAR COMO PECASH',
      customer: 'Cliente',
      amount: 'Monto',
      remaining: 'Disponible restante',
      approvalTime: 'Hora de aprobación',
      waitingFor: 'Esperando a',
      remaining_time: 'restante',
      resendNotification: 'Reenviar Notificación',
      cancelRequest: 'Cancelar Solicitud',
      markPreparing: 'Preparando',
      markOutForDelivery: 'En Camino',
      markDelivered: 'Entregado',
      markCompleted: 'Completado'
    },
    en: {
      title: 'Purchase Requests',
      newPurchase: 'New Purchase',
      pending: 'Pending',
      approved: 'Approved',
      history: 'History',
      noRequests: 'No pending requests',
      createFirst: 'Create your first purchase request',
      viewDetails: 'View details',
      okToDeliver: 'OK TO DELIVER',
      doNotProcess: 'DO NOT PROCESS AS PECASH',
      customer: 'Customer',
      amount: 'Amount',
      remaining: 'Remaining available',
      approvalTime: 'Approval time',
      waitingFor: 'Waiting for',
      remaining_time: 'remaining',
      resendNotification: 'Resend Notification',
      cancelRequest: 'Cancel Request',
      markPreparing: 'Preparing',
      markOutForDelivery: 'Out for Delivery',
      markDelivered: 'Delivered',
      markCompleted: 'Completed'
    }
  };
  const text = t[language] || t.es;

  // Fetch pending requests
  const fetchRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/purchase-requests/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch history requests (approved, declined, expired, cancelled)
  const fetchHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/purchase-requests/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistoryRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchRequests();
    fetchHistory();
    // Poll every 5 seconds for updates
    const interval = setInterval(fetchRequests, 5000);
    return () => clearInterval(interval);
  }, [fetchRequests, fetchHistory]);

  // Poll for specific request status
  useEffect(() => {
    if (!pollingRequest) return;
    
    const pollStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/purchase-requests/status/${pollingRequest}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.status !== 'pending_approval') {
            setSelectedRequest(data);
            setPollingRequest(null);
            fetchRequests();
            
            if (data.status === 'approved') {
              toast.success('✓ ' + (language === 'es' ? 'Compra Aprobada!' : 'Purchase Approved!'));
            } else if (data.status === 'declined') {
              toast.error('✗ ' + (language === 'es' ? 'Compra Rechazada' : 'Purchase Declined'));
            }
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    const interval = setInterval(pollStatus, 2000);
    return () => clearInterval(interval);
  }, [pollingRequest, token, language, fetchRequests]);

  const handleCancelRequest = async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/api/purchase-requests/cancel/${requestId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success(language === 'es' ? 'Solicitud cancelada' : 'Request cancelled');
        fetchRequests();
        setSelectedRequest(null);
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cancelar' : 'Failed to cancel');
    }
  };

  const handleUpdateDeliveryStatus = async (requestId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/purchase-requests/delivery-status`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ request_id: requestId, delivery_status: newStatus })
      });
      if (response.ok) {
        toast.success(language === 'es' ? 'Estado actualizado' : 'Status updated');
        fetchRequests();
        setSelectedRequest(null);
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error al actualizar' : 'Failed to update');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with New Purchase CTA */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800">{text.title}</h2>
        <Button 
          onClick={() => setShowNewRequest(true)}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="merchant-new-purchase"
        >
          <Plus className="w-5 h-5 mr-2" />
          {text.newPurchase}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-1">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'pending' 
              ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-600' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-pending"
        >
          {text.pending}
          {requests.length > 0 && (
            <span className="ml-2 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs">
              {requests.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('approved')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'approved' 
              ? 'bg-emerald-100 text-emerald-700 border-b-2 border-emerald-600' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-approved"
        >
          {text.approved}
          {historyRequests.filter(r => r.status === 'approved').length > 0 && (
            <span className="ml-2 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs">
              {historyRequests.filter(r => r.status === 'approved').length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'history' 
              ? 'bg-slate-100 text-slate-700 border-b-2 border-slate-600' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-history"
        >
          {text.history}
        </button>
      </div>

      {/* Pending Requests Tab */}
      {activeTab === 'pending' && (
      <div className="space-y-4">
        {requests.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-200">
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">{text.noRequests}</p>
              <p className="text-slate-400 text-sm mt-1">{text.createFirst}</p>
            </CardContent>
          </Card>
        ) : (
          requests.map((request) => (
            <motion.div
              key={request.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
            >
              {/* Status Banner */}
              {request.status === 'pending_approval' && (
                <div className="bg-amber-50 border-b border-amber-100 px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                    <span className="text-amber-800 font-medium">
                      {text.waitingFor} {request.beneficiary_name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-amber-700">
                    <Timer className="w-4 h-4" />
                    <span className="font-mono font-bold">
                      {formatTime(request.seconds_remaining || 0)}
                    </span>
                    <span className="text-sm">{text.remaining_time}</span>
                  </div>
                </div>
              )}

              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">{text.customer}</p>
                    <p className="text-lg font-semibold text-slate-800">{request.beneficiary_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-500 text-sm">{text.amount}</p>
                    <p className="text-2xl font-bold text-purple-600">
                      RD${request.requested_amount_dop?.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${STATUS_CONFIG[request.status]?.color}`}>
                    {language === 'es' ? STATUS_CONFIG[request.status]?.label : STATUS_CONFIG[request.status]?.labelEn}
                  </span>
                  {request.delivery_type === 'home_delivery' && (
                    <span className="flex items-center gap-1 text-blue-600 text-sm">
                      <Home className="w-4 h-4" />
                      {language === 'es' ? 'Entrega a domicilio' : 'Home Delivery'}
                    </span>
                  )}
                  {request.delivery_type === 'in_store' && (
                    <span className="flex items-center gap-1 text-green-600 text-sm">
                      <Store className="w-4 h-4" />
                      {language === 'es' ? 'En tienda' : 'In Store'}
                    </span>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-4">
                  {request.status === 'pending_approval' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleCancelRequest(request.id)}
                      >
                        {text.cancelRequest}
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedRequest(request);
                      if (request.status === 'pending_approval') {
                        setPollingRequest(request.id);
                      }
                    }}
                  >
                    {text.viewDetails}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
      )}

      {/* Approved Tab - Shows approved requests that may need delivery */}
      {activeTab === 'approved' && (
        <div className="space-y-4">
          {historyRequests.filter(r => r.status === 'approved').length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">
                  {language === 'es' ? 'Sin compras aprobadas' : 'No approved purchases'}
                </p>
              </CardContent>
            </Card>
          ) : (
            historyRequests.filter(r => r.status === 'approved').map((request) => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl border border-emerald-200 overflow-hidden shadow-sm"
              >
                <div className="bg-emerald-50 border-b border-emerald-100 px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-800 font-medium">
                      {language === 'es' ? 'Aprobado' : 'Approved'}
                    </span>
                  </div>
                  <span className="text-sm text-emerald-600">
                    {new Date(request.approved_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-slate-500 text-sm">{text.customer}</p>
                      <p className="font-semibold text-lg">{request.beneficiary_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-500 text-sm">{text.amount}</p>
                      <p className="font-bold text-2xl text-emerald-600">
                        RD${request.requested_amount_dop?.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      request.delivery_status === 'delivered' 
                        ? 'bg-emerald-100 text-emerald-700'
                        : request.delivery_status === 'out_for_delivery'
                          ? 'bg-blue-100 text-blue-700'
                          : request.delivery_status === 'preparing'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                    }`}>
                      {request.delivery_status === 'delivered' 
                        ? (language === 'es' ? 'Entregado' : 'Delivered')
                        : request.delivery_status === 'out_for_delivery'
                          ? (language === 'es' ? 'En Camino' : 'Out for Delivery')
                          : request.delivery_status === 'preparing'
                            ? (language === 'es' ? 'Preparando' : 'Preparing')
                            : (language === 'es' ? 'Pendiente entrega' : 'Pending delivery')}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedRequest(request)}
                    >
                      {text.viewDetails}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* History Tab - Shows all completed/declined/expired */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {historyRequests.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="py-12 text-center">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">
                  {language === 'es' ? 'Sin historial' : 'No history'}
                </p>
              </CardContent>
            </Card>
          ) : (
            historyRequests.map((request) => {
              const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.expired;
              const StatusIcon = statusConfig.icon;
              return (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
                >
                  <div className={`px-6 py-3 flex items-center justify-between ${
                    request.status === 'approved' ? 'bg-emerald-50 border-b border-emerald-100' :
                    request.status === 'declined' ? 'bg-red-50 border-b border-red-100' :
                    'bg-slate-50 border-b border-slate-100'
                  }`}>
                    <div className="flex items-center gap-2">
                      <StatusIcon className={`w-4 h-4 ${
                        request.status === 'approved' ? 'text-emerald-600' :
                        request.status === 'declined' ? 'text-red-600' :
                        'text-slate-500'
                      }`} />
                      <span className={`font-medium ${
                        request.status === 'approved' ? 'text-emerald-800' :
                        request.status === 'declined' ? 'text-red-800' :
                        'text-slate-600'
                      }`}>
                        {language === 'es' ? statusConfig.label : statusConfig.labelEn}
                      </span>
                    </div>
                    <span className="text-sm text-slate-500">
                      {new Date(request.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-slate-500 text-sm">{text.customer}</p>
                        <p className="font-semibold">{request.beneficiary_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-500 text-sm">{text.amount}</p>
                        <p className={`font-bold text-xl ${
                          request.status === 'approved' ? 'text-emerald-600' :
                          request.status === 'declined' ? 'text-red-600' :
                          'text-slate-600'
                        }`}>
                          RD${request.requested_amount_dop?.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedRequest(request)}
                      >
                        {text.viewDetails}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      )}

      {/* New Purchase Request Dialog */}
      <NewPurchaseRequestDialog
        isOpen={showNewRequest}
        onClose={() => setShowNewRequest(false)}
        token={token}
        language={language}
        onSuccess={(requestId) => {
          setShowNewRequest(false);
          fetchRequests();
          setPollingRequest(requestId);
        }}
      />

      {/* Request Details Dialog */}
      <RequestDetailsDialog
        request={selectedRequest}
        isOpen={!!selectedRequest}
        onClose={() => {
          setSelectedRequest(null);
          setPollingRequest(null);
        }}
        language={language}
        onCancel={handleCancelRequest}
        onUpdateDelivery={handleUpdateDeliveryStatus}
        text={text}
      />
    </div>
  );
}

// New Purchase Request Dialog
function NewPurchaseRequestDialog({ isOpen, onClose, token, language, onSuccess }) {
  const [step, setStep] = useState('find'); // find, scan, amount, confirm
  const [searchMethod, setSearchMethod] = useState('code'); // code, qr
  const [authCode, setAuthCode] = useState('');
  const [authorization, setAuthorization] = useState(null);
  const [amount, setAmount] = useState('');
  const [deliveryType, setDeliveryType] = useState('home_delivery');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scannerReady, setScannerReady] = useState(false);
  const scannerRef = React.useRef(null);

  const t = {
    es: {
      title: 'Nueva Solicitud de Compra',
      findCustomer: 'Buscar Cliente',
      enterCode: 'Ingresar Código de Autorización',
      codePlaceholder: 'PC-XXXX-XXXX',
      scanQR: 'Escanear QR',
      pasteCode: 'Pegar Código',
      customer: 'Cliente',
      available: 'Disponible',
      orderTotal: 'Total del Pedido',
      amountPlaceholder: '0.00',
      deliveryType: 'Tipo de Entrega',
      homeDelivery: 'Entrega a Domicilio',
      inStore: 'En Tienda',
      youAreRequesting: 'Estás solicitando',
      fromAvailable: 'del monto disponible de',
      sendRequest: 'Enviar Solicitud de Compra',
      back: 'Atrás',
      next: 'Siguiente',
      searching: 'Buscando...',
      sending: 'Enviando...',
      invalidCode: 'Código inválido',
      insufficientFunds: 'Monto insuficiente',
      enterValidAmount: 'Ingrese un monto válido'
    },
    en: {
      title: 'New Purchase Request',
      findCustomer: 'Find Customer',
      enterCode: 'Enter Authorization Code',
      codePlaceholder: 'PC-XXXX-XXXX',
      scanQR: 'Scan QR',
      pasteCode: 'Paste Code',
      customer: 'Customer',
      available: 'Available',
      orderTotal: 'Order Total',
      amountPlaceholder: '0.00',
      deliveryType: 'Delivery Type',
      homeDelivery: 'Home Delivery',
      inStore: 'In Store',
      youAreRequesting: 'You are requesting',
      fromAvailable: 'from available amount of',
      sendRequest: 'Send Purchase Request',
      back: 'Back',
      next: 'Next',
      searching: 'Searching...',
      sending: 'Sending...',
      invalidCode: 'Invalid code',
      insufficientFunds: 'Insufficient funds',
      enterValidAmount: 'Enter a valid amount'
    }
  };
  const text = t[language] || t.es;

  const handleValidateCode = async () => {
    if (!authCode.trim()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/purchase-requests/validate-code`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ authorization_code: authCode.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok && data.valid) {
        setAuthorization(data);
        setStep('amount');
      } else {
        setError(data.detail || text.invalidCode);
      }
    } catch (err) {
      setError(text.invalidCode);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError(text.enterValidAmount);
      return;
    }
    
    if (numAmount > authorization.available_amount_dop) {
      setError(text.insufficientFunds);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/purchase-requests/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          authorization_id: authorization.authorization_id,
          requested_amount_dop: numAmount,
          delivery_type: deliveryType
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(language === 'es' ? 'Solicitud enviada!' : 'Request sent!');
        onSuccess(data.request_id);
        resetForm();
      } else {
        setError(data.detail || 'Failed to create request');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep('find');
    setAuthCode('');
    setAuthorization(null);
    setAmount('');
    setDeliveryType('home_delivery');
    setError('');
    setScannerReady(false);
    // Clean up scanner if it exists
    if (scannerRef.current) {
      try {
        scannerRef.current.stop().catch(() => {});
      } catch (e) {}
      scannerRef.current = null;
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // QR Scanner setup
  const startScanner = async () => {
    setStep('scan');
    setError('');
    
    // Dynamically import Html5Qrcode to avoid SSR issues
    const { Html5Qrcode } = await import('html5-qrcode');
    
    // Wait for the element to be in DOM
    setTimeout(async () => {
      const scannerElement = document.getElementById('qr-reader');
      if (!scannerElement) return;
      
      try {
        const scanner = new Html5Qrcode('qr-reader');
        scannerRef.current = scanner;
        
        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 }
          },
          (decodedText) => {
            // QR scanned successfully
            handleQrCodeScanned(decodedText);
            scanner.stop().catch(() => {});
          },
          (errorMessage) => {
            // Ignore scan errors (no QR in frame)
          }
        );
        setScannerReady(true);
      } catch (err) {
        console.error('Scanner start error:', err);
        setError(language === 'es' 
          ? 'No se pudo acceder a la cámara. Verifica los permisos.' 
          : 'Could not access camera. Check permissions.');
        setStep('find');
      }
    }, 100);
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
    setScannerReady(false);
    setStep('find');
  };

  const handleQrCodeScanned = (qrData) => {
    // Parse the QR code - could be just the auth code or a URL containing it
    let code = qrData;
    
    // If it's a URL, try to extract the auth code
    if (qrData.includes('authorization_code=')) {
      const match = qrData.match(/authorization_code=([A-Z0-9]+)/i);
      if (match) code = match[1];
    } else if (qrData.includes('/redeem/')) {
      // Legacy URL format
      const parts = qrData.split('/redeem/');
      if (parts[1]) code = parts[1].split('?')[0];
    }
    
    // Clean the code - take only alphanumeric characters
    code = code.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    
    if (code.length >= 6) {
      setAuthCode(code);
      setScannerReady(false);
      setStep('find');
      // Auto-validate the scanned code
      setTimeout(() => {
        document.querySelector('[data-testid="validate-code-btn"]')?.click();
      }, 300);
    } else {
      setError(language === 'es' ? 'Código QR inválido' : 'Invalid QR code');
    }
  };

  const formatAmount = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return num.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-purple-600" />
            {text.title}
          </DialogTitle>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {/* Step 1: Find Customer */}
          {step === 'find' && (
            <motion.div
              key="find"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              {/* Method selector */}
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => setSearchMethod('code')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    searchMethod === 'code' 
                      ? 'bg-purple-100 text-purple-700 border-2 border-purple-300' 
                      : 'bg-slate-100 text-slate-600 border-2 border-transparent'
                  }`}
                >
                  {text.pasteCode}
                </button>
                <button
                  onClick={() => { setSearchMethod('qr'); startScanner(); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    searchMethod === 'qr' 
                      ? 'bg-purple-100 text-purple-700 border-2 border-purple-300' 
                      : 'bg-slate-100 text-slate-600 border-2 border-transparent'
                  }`}
                  data-testid="scan-qr-btn"
                >
                  <Camera className="w-4 h-4" />
                  {text.scanQR}
                </button>
              </div>

              <div>
                <Label>{text.enterCode}</Label>
                <Input
                  value={authCode}
                  onChange={(e) => setAuthCode(e.target.value.toUpperCase())}
                  placeholder={text.codePlaceholder}
                  className="mt-2 text-center font-mono text-lg tracking-wider"
                  data-testid="merchant-auth-code-input"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <Button
                onClick={handleValidateCode}
                disabled={!authCode.trim() || loading}
                className="w-full bg-purple-600 hover:bg-purple-700"
                data-testid="validate-code-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {loading ? text.searching : text.next}
              </Button>
            </motion.div>
          )}

          {/* Step: QR Scanner */}
          {step === 'scan' && (
            <motion.div
              key="scan"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="relative">
                <div 
                  id="qr-reader" 
                  className="w-full rounded-xl overflow-hidden bg-black"
                  style={{ minHeight: '300px' }}
                />
                {!scannerReady && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50 rounded-xl">
                    <div className="text-center text-white">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                      <p className="text-sm">{language === 'es' ? 'Iniciando cámara...' : 'Starting camera...'}</p>
                    </div>
                  </div>
                )}
              </div>
              
              <p className="text-center text-sm text-slate-500">
                {language === 'es' 
                  ? 'Apunta la cámara al código QR de la autorización' 
                  : 'Point camera at authorization QR code'}
              </p>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <Button
                variant="outline"
                onClick={stopScanner}
                className="w-full"
              >
                <CloseIcon className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
            </motion.div>
          )}

          {/* Step 2: Enter Amount */}
          {step === 'amount' && authorization && (
            <motion.div
              key="amount"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              {/* Customer Info */}
              <div className="bg-slate-50 p-4 rounded-xl">
                <p className="text-slate-500 text-sm">{text.customer}</p>
                <p className="text-lg font-semibold text-slate-800">{authorization.beneficiary_name}</p>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-slate-500 text-sm">{text.available}:</span>
                  <span className="text-emerald-600 font-bold text-xl">
                    RD${authorization.available_amount_dop?.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              {/* Amount Input */}
              <div>
                <Label>{text.orderTotal}</Label>
                <div className="relative mt-2">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">RD$</span>
                  <Input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={text.amountPlaceholder}
                    className="pl-14 text-right text-2xl font-bold h-14"
                    data-testid="merchant-purchase-amount"
                  />
                </div>
                
                {/* Amount Preview */}
                {amount && parseFloat(amount) > 0 && (
                  <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-purple-800 text-center">
                      {text.youAreRequesting} <strong>RD${formatAmount(amount)}</strong>
                      <br />
                      <span className="text-sm text-purple-600">
                        {text.fromAvailable} {authorization.beneficiary_name}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* Delivery Type */}
              <div>
                <Label>{text.deliveryType}</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    onClick={() => setDeliveryType('home_delivery')}
                    className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${
                      deliveryType === 'home_delivery' 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Home className={`w-6 h-6 ${deliveryType === 'home_delivery' ? 'text-purple-600' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium ${deliveryType === 'home_delivery' ? 'text-purple-800' : 'text-slate-600'}`}>
                      {text.homeDelivery}
                    </span>
                  </button>
                  <button
                    onClick={() => setDeliveryType('in_store')}
                    className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${
                      deliveryType === 'in_store' 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Store className={`w-6 h-6 ${deliveryType === 'in_store' ? 'text-purple-600' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium ${deliveryType === 'in_store' ? 'text-purple-800' : 'text-slate-600'}`}>
                      {text.inStore}
                    </span>
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep('find')} className="flex-1">
                  {text.back}
                </Button>
                <Button
                  onClick={handleCreateRequest}
                  disabled={!amount || parseFloat(amount) <= 0 || loading}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  data-testid="merchant-send-purchase-request"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {loading ? text.sending : text.sendRequest}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

// Request Details Dialog
function RequestDetailsDialog({ request, isOpen, onClose, language, onCancel, onUpdateDelivery, text }) {
  if (!request) return null;

  const isApproved = request.status === 'approved';
  const isDeclined = request.status === 'declined';
  const isPending = request.status === 'pending_approval';

  const dialogTitle = isPending 
    ? (language === 'es' ? 'Esperando Aprobación' : 'Waiting for Approval')
    : isApproved 
      ? (language === 'es' ? 'Compra Aprobada' : 'Purchase Approved')
      : (language === 'es' ? 'Compra Rechazada' : 'Purchase Declined');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle className="sr-only">{dialogTitle}</DialogTitle>
        
        {/* Approved State - Big Success */}
        {isApproved && (
          <div className="text-center py-6">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-emerald-800 mb-1">
              {language === 'es' ? 'COMPRA APROBADA' : 'PURCHASE APPROVED'}
            </h2>
            
            <p className="text-slate-500 mb-6">{request.beneficiary_name}</p>
            
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6 mb-4">
              <p className="text-emerald-600 text-sm mb-1">{text.amount}</p>
              <p className="text-4xl font-bold text-emerald-800">
                RD${request.requested_amount_dop?.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
              </p>
            </div>
            
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500">{text.remaining}</span>
                <span className="font-semibold">
                  RD${request.remaining_after_dop?.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{text.approvalTime}</span>
                <span className="font-semibold">
                  {new Date(request.approved_at).toLocaleTimeString()}
                </span>
              </div>
            </div>
            
            {/* OK to Deliver Banner */}
            <div className="mt-6 bg-emerald-600 text-white rounded-2xl p-6">
              <Truck className="w-8 h-8 mx-auto mb-2" />
              <p className="text-xl font-bold">{text.okToDeliver}</p>
            </div>

            {/* Delivery Status Buttons */}
            {request.delivery_type === 'home_delivery' && (
              <div className="mt-4 space-y-2">
                {request.delivery_status === 'none' && (
                  <Button 
                    className="w-full" 
                    onClick={() => onUpdateDelivery(request.id, 'preparing')}
                  >
                    {text.markPreparing}
                  </Button>
                )}
                {request.delivery_status === 'preparing' && (
                  <Button 
                    className="w-full" 
                    onClick={() => onUpdateDelivery(request.id, 'out_for_delivery')}
                  >
                    <Truck className="w-4 h-4 mr-2" />
                    {text.markOutForDelivery}
                  </Button>
                )}
                {request.delivery_status === 'out_for_delivery' && (
                  <Button 
                    className="w-full" 
                    onClick={() => onUpdateDelivery(request.id, 'delivered')}
                  >
                    {text.markDelivered}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Declined State */}
        {isDeclined && (
          <div className="text-center py-6">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-red-800 mb-1">
              {language === 'es' ? 'COMPRA RECHAZADA' : 'PURCHASE DECLINED'}
            </h2>
            
            <p className="text-slate-500 mb-6">{request.beneficiary_name}</p>
            
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
              <p className="text-red-800 font-bold text-lg">{text.doNotProcess}</p>
            </div>
          </div>
        )}

        {/* Pending State */}
        {isPending && (
          <div className="text-center py-6">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-10 h-10 text-amber-600 animate-spin" />
            </div>
            
            <h2 className="text-xl font-bold text-amber-800 mb-1">
              {language === 'es' ? 'Esperando Aprobación' : 'Waiting for Approval'}
            </h2>
            
            <p className="text-slate-500 mb-4">{request.beneficiary_name}</p>
            
            <div className="bg-slate-50 rounded-xl p-4 mb-4">
              <p className="text-slate-500 text-sm mb-1">{text.amount}</p>
              <p className="text-3xl font-bold text-purple-600">
                RD${request.requested_amount_dop?.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
              </p>
            </div>
            
            {request.seconds_remaining > 0 && (
              <div className="flex items-center justify-center gap-2 text-amber-700 mb-4">
                <Timer className="w-5 h-5" />
                <span className="font-mono font-bold text-2xl">
                  {Math.floor(request.seconds_remaining / 60)}:{(request.seconds_remaining % 60).toString().padStart(2, '0')}
                </span>
              </div>
            )}
            
            {/* Contact Beneficiary - Secure flow: approval link sent via notification only */}
            <div className="space-y-2 mb-4">
              <p className="text-sm text-slate-500 mb-2">
                {language === 'es' 
                  ? 'El beneficiario recibió el enlace de aprobación por notificación.' 
                  : 'Beneficiary received approval link via notification.'}
              </p>
              
              {/* WhatsApp reminder to beneficiary */}
              {request.beneficiary_phone && (
                <Button
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    const phone = request.beneficiary_phone.replace(/\D/g, '');
                    const message = language === 'es' 
                      ? `Hola! Te enviamos una solicitud de compra por RD$${request.requested_amount_dop?.toLocaleString('es-DO', { minimumFractionDigits: 2 })}. Por favor revisa tu correo o SMS para aprobarla. Gracias!`
                      : `Hi! We sent you a purchase request for RD$${request.requested_amount_dop?.toLocaleString('es-DO', { minimumFractionDigits: 2 })}. Please check your email or SMS to approve it. Thanks!`;
                    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
                  }}
                  data-testid="whatsapp-remind-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Recordar por WhatsApp' : 'Remind via WhatsApp'}
                </Button>
              )}
              
              {/* Call beneficiary */}
              {request.beneficiary_phone && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    window.location.href = `tel:${request.beneficiary_phone}`;
                  }}
                  data-testid="call-beneficiary-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Llamar al Beneficiario' : 'Call Beneficiary'}
                </Button>
              )}
              
              {!request.beneficiary_phone && (
                <p className="text-xs text-slate-400 text-center">
                  {language === 'es' 
                    ? 'No hay número de teléfono registrado para este beneficiario.' 
                    : 'No phone number registered for this beneficiary.'}
                </p>
              )}
            </div>
            
            <Button 
              variant="outline"
              className="w-full text-red-600 border-red-200"
              onClick={() => onCancel(request.id)}
            >
              {text.cancelRequest}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
